import { HISTORICAL_METHODS } from '../../consumer/historical/contract';
import type {
  ExternalDay,
  ExternalFilter,
  ExternalGroup,
  ExternalLedgerFilter,
  ExternalPageResult,
  ExternalPort,
  ExternalRow,
  ExternalRun,
} from '../../consumer/historical/external-contract';
import {
  EXTERNAL_TRACKS,
  validExternalGroupId,
  validExternalRunId,
} from '../../consumer/historical/external-contract';
import { createMonitoringRequest, MonitoringReadError } from '../http';
import {
  parseExternalDays,
  parseExternalGroup,
  parseExternalGroups,
  parseExternalRows,
  parseExternalRun,
  parseExternalRuns,
} from './external-wire';
export class ExternalRequestError extends MonitoringReadError {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super('HTTP');
  }
}
export function externalPageQuery(offset: number, limit: number): URLSearchParams {
  if (
    !Number.isSafeInteger(offset) ||
    offset < 0 ||
    offset > 2000000 ||
    !Number.isSafeInteger(limit) ||
    limit < 1 ||
    limit > 100
  )
    throw new Error('Invalid external pagination');
  return new URLSearchParams({ offset: String(offset), limit: String(limit) });
}
export function externalQuery(f: ExternalFilter): string {
  const p = externalPageQuery(f.offset, f.limit);
  if (
    (f.track && !EXTERNAL_TRACKS.some((t) => t === f.track)) ||
    (f.testYear && !['2024', '2025'].includes(f.testYear)) ||
    (f.metric && !['TEMPERATURE', 'WIND_SPEED'].includes(f.metric)) ||
    (f.method && !HISTORICAL_METHODS.some((m) => m === f.method)) ||
    (f.horizonHours && !['1', '3', '6'].includes(f.horizonHours)) ||
    (f.donorStationId && !['AEI0000OMAA', 'AEI0000OMDB'].includes(f.donorStationId)) ||
    (f.track === EXTERNAL_TRACKS[1] && f.testYear && f.testYear !== '2025') ||
    (f.track === EXTERNAL_TRACKS[0] && f.donorStationId)
  )
    throw new Error('Invalid external filter');
  for (const k of ['climateContext', 'country', 'stationId'] as const)
    if (f[k] && !/^[A-Za-z0-9_ -]{1,128}$/.test(f[k]))
      throw new Error('Invalid external cohort filter');
  for (const [k, v] of Object.entries(f)) if (v !== '') p.set(k, String(v));
  return p.toString();
}
export function externalLedgerQuery(f: ExternalLedgerFilter, errors = false): string {
  const p = externalPageQuery(f.offset, f.limit);
  if (!validExternalGroupId(f.groupId)) throw new Error('Invalid external group');
  p.set('groupId', f.groupId);
  if (errors && (f.from || f.to)) throw new Error('Errors route has no date filter');
  for (const k of ['from', 'to'] as const)
    if (f[k]) {
      const v = f[k];
      if (
        !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/.test(v) ||
        !Number.isFinite(Date.parse(v)) ||
        new Date(v).toISOString().slice(0, 10) !== v.slice(0, 10)
      )
        throw new Error('Invalid UTC date');
      p.set(k, v);
    }
  if (f.from && f.to && Date.parse(f.from) >= Date.parse(f.to))
    throw new Error('Invalid date range');
  return p.toString();
}
export function createExternalAdapter(options: {
  baseOrigin: string;
  fetchImpl?: typeof fetch;
}): ExternalPort {
  const request = createMonitoringRequest(options);
  const root = '/read-models/normal-monitoring/historical/external';
  const failure = (status: number, v: unknown) => {
    if (!v || typeof v !== 'object' || Array.isArray(v)) return new MonitoringReadError('WIRE');
    const o = v as Record<string, unknown>;
    return Object.keys(o).sort().join(',') === 'error,message' &&
      typeof o.error === 'string' &&
      typeof o.message === 'string' &&
      o.message.length <= 4096 &&
      ((status === 400 && o.error === 'INVALID_EXTERNAL_QUERY') ||
        (status === 404 &&
          [
            'EXTERNAL_RUN_NOT_FOUND',
            'EXTERNAL_GROUP_NOT_FOUND',
            'EXTERNAL_ROUTE_NOT_FOUND',
          ].includes(o.error)) ||
        (status === 405 && o.error === 'EXTERNAL_READ_ONLY') ||
        (status === 503 && o.error === 'EXTERNAL_STORAGE_UNAVAILABLE'))
      ? new ExternalRequestError(status, o.error)
      : new MonitoringReadError('WIRE');
  };
  const id = (v: string) => {
    if (!validExternalRunId(v)) throw new Error('Invalid external run');
    return v;
  };
  const gid = (v: string) => {
    if (!validExternalGroupId(v)) throw new Error('Invalid external group');
    return v;
  };
  const checked = <T>(p: ExternalPageResult<T>, offset: number, limit: number) => {
    if (p.offset !== offset || p.limit !== limit) throw new MonitoringReadError('WIRE');
    return p;
  };
  async function ledger<T extends ExternalRow | ExternalDay>(
    value: string,
    f: ExternalLedgerFilter,
    signal: AbortSignal,
    route: string,
    parse: (v: unknown) => ExternalPageResult<T>,
  ) {
    const p = checked(
      await request(
        `${root}/runs/${id(value)}/${route}?${externalLedgerQuery(f, route === 'errors')}`,
        parse,
        signal,
        undefined,
        failure,
      ),
      f.offset,
      f.limit,
    );
    if (
      p.items.some((r) => {
        const at = 'targetAt' in r ? r.targetAt : r.day + 'T00:00:00Z';
        return (
          r.groupId !== f.groupId ||
          (f.from && Date.parse(at) < Date.parse(f.from)) ||
          (f.to && Date.parse(at) >= Date.parse(f.to))
        );
      })
    )
      throw new MonitoringReadError('WIRE');
    return p;
  }
  return {
    runs: async (offset, limit, signal) =>
      checked(
        await request<ExternalPageResult<ExternalRun>>(
          `${root}/runs?${externalPageQuery(offset, limit)}`,
          parseExternalRuns,
          signal,
          undefined,
          failure,
        ),
        offset,
        limit,
      ),
    run: async (value, signal) => {
      const r = await request(
        `${root}/runs/${id(value)}`,
        parseExternalRun,
        signal,
        undefined,
        failure,
      );
      if (r.runId !== value) throw new MonitoringReadError('WIRE');
      return r;
    },
    groups: async (value, f, signal) => {
      const p = checked(
        await request<ExternalPageResult<ExternalGroup>>(
          `${root}/runs/${id(value)}/groups?${externalQuery(f)}`,
          parseExternalGroups,
          signal,
          undefined,
          failure,
        ),
        f.offset,
        f.limit,
      );
      if (
        p.items.some((g) =>
          Object.entries(f).some(
            ([k, v]) =>
              v !== '' &&
              k !== 'offset' &&
              k !== 'limit' &&
              String(g[k as keyof ExternalGroup] ?? '') !== String(v),
          ),
        )
      )
        throw new MonitoringReadError('WIRE');
      return p;
    },
    group: async (value, groupId, signal) => {
      const g = await request(
        `${root}/runs/${id(value)}/groups/${gid(groupId)}`,
        parseExternalGroup,
        signal,
        undefined,
        failure,
      );
      if (g.groupId !== groupId) throw new MonitoringReadError('WIRE');
      return g;
    },
    rows: (value, f, signal) => ledger(value, f, signal, 'rows', parseExternalRows),
    errors: (value, f, signal) => ledger(value, f, signal, 'errors', parseExternalRows),
    days: (value, f, signal) => ledger(value, f, signal, 'days', parseExternalDays),
  };
}
